import { useState, useEffect, useRef, useMemo } from 'react';
import AddPersonModal from '../modals/AddPersonModal';
import ImportBgChecksModal from '../modals/ImportBgChecksModal';
import MergePeopleModal from '../modals/MergePeopleModal';
import PassLevelMultiSelect from '../shared/PassLevelMultiSelect';
import { getPeople, upsertPerson, deletePerson, sendScheduleLink, getTeams, getAllTeamMemberships, getTeamMembers, addTeamMember, removeTeamMember, invalidateCache, syncPassLevelsFromTeams, syncBrushfireDeep, getCheckIns, checkInPerson, uncheckInPerson, getAllCheckInCards, getBlocks, getAssignments } from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import { canUserSend } from '../../config';
import { usePassLevels } from '../../hooks/usePassLevels';
import { personScheduleUrl } from '../../utils/scheduleFormat';

const COPY_ICON = (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <rect x="4.5" y="4.5" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M3 8.5H2a1 1 0 01-1-1V2a1 1 0 011-1h5.5a1 1 0 011 1v1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const SEND_ICON = (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M1 7l11-5.5-5.5 11L5 8 1 7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);

// Multi-select team dropdown for the check-in sub-filter.
function TeamFilterDropdown({ teams, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedNames = teams.filter(t => selected.includes(t.id)).map(t => t.name);
  const label = selected.length === 0 ? 'All Teams'
    : selected.length === 1 ? selectedNames[0]
    : `${selected.length} teams`;

  function toggle(teamId) {
    onChange(selected.includes(teamId)
      ? selected.filter(id => id !== teamId)
      : [...selected, teamId]);
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className={`filter-btn${selected.length ? ' active' : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        {label} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: 220, maxHeight: 280, overflowY: 'auto',
        }}>
          <div
            style={{ padding: '9px 14px', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderBottom: '1px solid var(--border)', color: selected.length ? 'var(--black)' : 'var(--muted)' }}
            onClick={() => onChange([])}
          >
            All Teams{selected.length ? ' (clear selection)' : ''}
          </div>
          {teams.map(t => (
            <label
              key={t.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', fontFamily: 'var(--font-b)', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--off)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <input
                type="checkbox"
                checked={selected.includes(t.id)}
                onChange={() => toggle(t.id)}
                style={{ accentColor: 'var(--black)' }}
              />
              {t.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline "Add to Team" dropdown for a single person
function AddToTeamDropdown({ personId, teams, currentTeamIds, onAdd, showToast }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const available = teams.filter(t => !currentTeamIds.includes(t.id));

  async function handleSelect(team) {
    setOpen(false);
    setSaving(true);
    try {
      await addTeamMember(team.id, personId);
      invalidateCache('people');
      showToast(`Added to ${team.name}`);
      onAdd(team);
    } catch (err) {
      showToast('Error adding to team');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn-remove"
        title="Add to team"
        onClick={() => setOpen(v => !v)}
        style={{ color: 'var(--mid)', padding: '2px 5px', fontSize: 16, lineHeight: 1 }}
        disabled={saving}
      >
        {saving ? '…' : '+'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200,
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: 180, maxHeight: 220, overflowY: 'auto',
        }}>
          {available.length === 0 ? (
            <div style={{ padding: '10px 14px', fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
              Already on all teams
            </div>
          ) : available.map(team => (
            <div
              key={team.id}
              style={{ padding: '9px 14px', fontFamily: 'var(--font-b)', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => handleSelect(team)}
            >
              {team.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPeople({ openPersonPanel, showToast }) {
  const { user } = useAuth();
  const passLevels = usePassLevels();
  const [people, setPeople] = useState([]);
  const [checkIns, setCheckIns] = useState({}); // personId → { date, time } latest door check-in
  const [ciBusy, setCiBusy] = useState({});     // personId → true while a manual check-in/undo saves
  const [ciTeamFilter, setCiTeamFilter] = useState([]); // team ids selected in the check-in sub-filter ([] = all)
  const [csvBusy, setCsvBusy] = useState(false);
  const [lcCsvBusy, setLcCsvBusy] = useState(false);

  // Security list for Leadership Co's Wed/Thu servers: they skip early
  // check-in (treated like a normal Sunday, serving stickers only), so the
  // door needs a printable roster to verify who belongs backstage — names +
  // badge access, no meals.
  async function handleDownloadLcSecurityCsv() {
    setLcCsvBusy(true);
    try {
      const LC_DATES = { '2026-09-16': 'Wednesday', '2026-09-17': 'Thursday' };
      const [teamsData, blocks, people] = await Promise.all([getTeams(), getBlocks(), getPeople()]);
      const lcTeams = (teamsData || []).filter(t => /leadership\s*co/i.test(t.name || ''));
      const lcKeys = new Set(lcTeams.flatMap(t => [t.id, t.name]).filter(Boolean));
      // Blocks key teamId by uuid OR by team name depending on how the team
      // was created — match either, plus a direct name test for safety.
      const lcBlocks = (blocks || []).filter(b =>
        LC_DATES[String(b.date)] &&
        (lcKeys.has(b.teamId) || /leadership\s*co/i.test(String(b.teamId || ''))));
      if (!lcBlocks.length) { showToast && showToast('No Leadership Co blocks found on Sept 16–17.'); return; }
      const assignLists = await Promise.all(lcBlocks.map(b => getAssignments(b.id).catch(() => [])));
      const byPerson = {}; // personId → { days: Set, blocks: Set }
      assignLists.forEach((list, i) => {
        const day = LC_DATES[String(lcBlocks[i].date)];
        (list || []).forEach(a => {
          if (!a.personId) return;
          if (String(a.status || '').trim().toLowerCase() === 'declined') return;
          const rec = byPerson[a.personId] = byPerson[a.personId] || { days: new Set(), blocks: new Set() };
          rec.days.add(day);
          rec.blocks.add(lcBlocks[i].title || '');
        });
      });
      const pmap = {};
      (people || []).forEach(p => { pmap[p.id] = p; });
      const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
      const splitName = (full) => {
        const parts = String(full || '').trim().split(/\s+/);
        return [parts[0] || '', parts.slice(1).join(' ')];
      };
      const rows = Object.keys(byPerson)
        .map(pid => {
          const p = pmap[pid];
          const [first, last] = splitName(p ? p.name : pid);
          const days = ['Wednesday', 'Thursday'].filter(d => byPerson[pid].days.has(d)).join(' + ');
          const badge = p ? String(p.passLevel || '').split(',').map(b => b.trim()).filter(Boolean).join(', ') : '';
          return { first, last, cols: [first, last, badge || '—', days] };
        })
        .sort((a, b) => (a.last || a.first).localeCompare(b.last || b.first) || a.first.localeCompare(b.first));
      const header = ['First Name', 'Last Name', 'Badge Access', 'Serving'];
      const csv = [header, ...rows.map(r => r.cols)].map(r => r.map(esc).join(',')).join('\r\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `leadership-co-security-wed-thu.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      showToast && showToast(`Downloaded ${rows.length} Leadership Co servers.`);
    } catch (err) {
      console.error('LC security CSV failed:', err);
      showToast && showToast('Could not build the Leadership Co list — try again.');
    } finally {
      setLcCsvBusy(false);
    }
  }

  async function handleManualCheckIn(person) {
    setCiBusy(prev => ({ ...prev, [person.id]: true }));
    try {
      const r = await checkInPerson(person.id);
      setCheckIns(prev => ({ ...prev, [person.id]: { date: r?.date || '', time: r?.time || '' } }));
    } catch (err) {
      console.error('Manual check-in failed:', err);
      showToast && showToast('Check-in did not save — try again.');
    } finally {
      setCiBusy(prev => ({ ...prev, [person.id]: false }));
    }
  }

  async function handleManualUncheck(person) {
    if (!window.confirm(`Remove today's check-in for ${person.name}?`)) return;
    setCiBusy(prev => ({ ...prev, [person.id]: true }));
    try {
      await uncheckInPerson(person.id);
      setCheckIns(prev => {
        const next = { ...prev };
        delete next[person.id];
        return next;
      });
    } catch (err) {
      console.error('Undo check-in failed:', err);
      showToast && showToast('Could not undo the check-in — try again.');
    } finally {
      setCiBusy(prev => ({ ...prev, [person.id]: false }));
    }
  }

  // Analog backup: one printable CSV with everything the check-in station
  // shows — names, teams, badges, radio, meals written out per day — plus a
  // blank column to check people off by hand if Wi-Fi dies.
  async function handleDownloadCheckInCsv() {
    setCsvBusy(true);
    try {
      const d = await getAllCheckInCards();
      const cards = d?.results || [];
      if (!cards.length) { showToast && showToast('No check-in data returned — redeploy the Apps Script first.'); return; }
      const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
      const mealText = (meals) => {
        const DAY_FULL = { Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };
        const lines = ['Thu', 'Fri', 'Sat', 'Sun'].map(dd => {
          const m = (meals || {})[dd] || {};
          const got = ['Breakfast', 'Lunch', 'Dinner'].filter(mt => m[mt]);
          return got.length ? `${DAY_FULL[dd]}: ${got.join(', ')}` : null;
        }).filter(Boolean);
        return lines.length ? lines.join('\n') : 'No meals';
      };
      const splitName = (full) => {
        const parts = String(full || '').trim().split(/\s+/);
        return [parts[0] || '', parts.slice(1).join(' ')];
      };
      const rows = cards
        .map(c => {
          const [first, last] = splitName(c.name);
          const teamList = [c.team, ...(Array.isArray(c.alsoOn) ? c.alsoOn : [])].filter(Boolean).join(', ');
          const badges = (c.badges && c.badges.length ? c.badges : String(c.passLevel || '').split(',').map(b => b.trim()).filter(Boolean)).join(', ');
          const ci = checkIns[c.id];
          return { first, last, cols: [first, last, teamList, badges, c.needsRadio ? 'RADIO' : '', mealText(c.meals), ci ? `✓ ${ci.time || ci.date}` : ''] };
        })
        .sort((a, b) => (a.last || a.first).localeCompare(b.last || b.first) || a.first.localeCompare(b.first));
      const header = ['First Name', 'Last Name', 'Teams', 'Badges / Passes', 'Radio', 'Meals', 'Checked In'];
      const csv = [header, ...rows.map(r => r.cols)].map(r => r.map(esc).join(',')).join('\r\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `checkin-backup-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('Check-in CSV failed:', err);
      showToast && showToast('Could not build the CSV — is the latest Apps Script deployed?');
    } finally {
      setCsvBusy(false);
    }
  }
  const [teams, setTeams] = useState([]);
  const [memberships, setMemberships] = useState({}); // { personId: [{teamId, teamName}] }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [bgImportOpen, setBgImportOpen] = useState(false);
  const [mergeGroup, setMergeGroup] = useState(null); // duplicate cluster being merged

  function openMerge(person) {
    // Same rule as the duplicate flag: identical name, or shared email with a
    // SIMILAR name — a spouse on the same inbox never joins the merge group.
    const e = effEmail(person);
    const group = people.filter(p => {
      if (p.id === person.id) return true;
      if (normName(p.name) && normName(p.name) === normName(person.name)) return true;
      return e && effEmail(p) === e && namesSimilar(p.name, person.name);
    });
    setMergeGroup(group);
  }
  const [nameSort, setNameSort] = useState(''); // '' = sheet order, 'asc' | 'desc'
  const [passLevelOverrides, setPassLevelOverrides] = useState({});
  const [syncing, setSyncing] = useState(false);
  const hasSyncedRef = useRef(false);

  const canSend = canUserSend(user?.email, user?.role);

  function loadAll() {
    setLoading(true);
    invalidateCache('people');
    Promise.all([getPeople(), getTeams(), getCheckIns().catch(() => ({}))])
      .then(([peopleData, teamsData, checkInsData]) => {
        const list = (peopleData || []).filter(p => p.name || p.email);
        setPeople(list);
        setTeams(teamsData || []);
        setCheckIns(checkInsData || {});

        // Build a name lookup from the teams we just loaded
        const teamNameMap = {};
        (teamsData || []).forEach(t => { teamNameMap[t.id] = t.name; });

        // Try new getAllTeamMemberships endpoint first;
        // fall back to getTeamMembers() (no arg = all) which works with existing GAS deployment
        getAllTeamMemberships()
          .catch(() =>
            getTeamMembers(undefined).then(allMembers => {
              const map = {};
              (allMembers || []).forEach(m => {
                if (!m.personId || !m.teamId) return;
                if (!map[m.personId]) map[m.personId] = [];
                if (!map[m.personId].some(x => x.teamId === m.teamId))
                  map[m.personId].push({ teamId: m.teamId, teamName: teamNameMap[m.teamId] || m.teamId });
              });
              return map;
            })
          )
          .then(data => setMemberships(data || {}))
          .catch(() => setMemberships({}));
      })
      .catch(err => console.error('Failed to load people:', err))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadAll();
    // Auto-sync pass levels from teams on first load
    if (!hasSyncedRef.current) {
      hasSyncedRef.current = true;
      syncPassLevelsFromTeams()
        .then(() => {
          invalidateCache('people');
          getPeople().then(data => {
            setPeople((data || []).filter(p => p.name || p.email));
          });
        })
        .catch(() => {}); // silent — GAS may not have this endpoint yet
    }
  }, []);

  async function handlePassLevelChange(person, newLevel) {
    setPassLevelOverrides(prev => ({ ...prev, [person.id]: newLevel }));
    try {
      // passLevelManual locks this value so the team auto-sync never overrides it
      await upsertPerson({ ...person, passLevel: newLevel, passLevelManual: true });
    } catch (err) {
      console.error('Failed to update pass level:', err);
      setPassLevelOverrides(prev => ({ ...prev, [person.id]: person.passLevel }));
    }
  }

  function handleCopyLink(person) {
    const link = personScheduleUrl(person.id);
    navigator.clipboard.writeText(link).catch(() => {});
    showToast('Link copied to clipboard');
  }

  async function handleSendLink(person) {
    if (!canSend) {
      showToast('Only @thebelonging.co staff can send schedule links.');
      return;
    }
    try {
      showToast(`Sending schedule link to ${person.name}…`);
      await sendScheduleLink(person.email, person.name, person.id, 'schedule-link', user.email);
      showToast(`Schedule link sent to ${person.name}.`);
    } catch (err) {
      showToast('Error sending email — check the Apps Script logs.');
      console.error(err);
    }
  }

  async function handleSyncBrushfire() {
    if (syncing) return;
    setSyncing(true);
    showToast('Syncing from Brushfire…');
    try {
      // Deep sweep: reconciles everyone's current teams, so Kat's team moves in
      // Brushfire land even when Brushfire didn't bump the record's Last Modified.
      const r = await syncBrushfireDeep();
      if (r?.skipped) {
        showToast(r.reason || 'A sync is already running — try again in a minute.');
        return;
      }
      const parts = [];
      if (r.newPeople) parts.push(`${r.newPeople} new`);
      if (r.primaryAdded || r.primaryChanged) parts.push(`${(r.primaryAdded || 0) + (r.primaryChanged || 0)} main-team update${(r.primaryAdded || 0) + (r.primaryChanged || 0) === 1 ? '' : 's'}`);
      if (r.secondarySeeded) parts.push(`${r.secondarySeeded} team${r.secondarySeeded === 1 ? '' : 's'} seeded`);
      if (r.statusChanges) parts.push(`${r.statusChanges} status change${r.statusChanges === 1 ? '' : 's'}`);
      showToast(parts.length ? `✓ Brushfire sync done: ${parts.join(', ')}.` : '✓ Brushfire sync done — everything already up to date.');
      loadAll();
    } catch (err) {
      // A long first sweep can outlive the page's wait even though it keeps
      // running on Google's side — don't scare anyone with a false failure.
      console.error('Brushfire sync error:', err);
      showToast('Still working — a long sync keeps running in the background. Wait 2–3 minutes, then refresh this page.');
      setTimeout(() => loadAll(), 150000);
    } finally {
      setSyncing(false);
    }
  }

  async function handleAddPerson(newPerson) {
    try {
      await upsertPerson(newPerson);
      showToast('Person added.');
      loadAll();
    } catch (err) {
      showToast('Error adding person.');
      console.error(err);
    }
  }

  function handleMembershipAdded(personId, team) {
    setMemberships(prev => {
      const existing = prev[personId] || [];
      if (existing.some(m => m.teamId === team.id)) return prev;
      return { ...prev, [personId]: [...existing, { teamId: team.id, teamName: team.name }] };
    });
    // Mirror GAS auto-set: apply team's pass level immediately if person has none yet
    if (team.passLevel) {
      const person = people.find(p => p.id === personId);
      const currentLevel = passLevelOverrides[personId] ?? (person?.passLevel || '');
      if (!currentLevel) {
        setPassLevelOverrides(prev => ({ ...prev, [personId]: team.passLevel }));
      }
    }
  }

  async function handleMembershipRemoved(person, tm) {
    // Optimistic removal with rollback — mirrors handleMembershipAdded.
    const prevList = memberships[person.id] || [];
    setMemberships(prev => ({
      ...prev,
      [person.id]: (prev[person.id] || []).filter(m => m.teamId !== tm.teamId),
    }));
    try {
      await removeTeamMember(tm.teamId, person.id);
      showToast(`${person.name} removed from ${tm.teamName}.`);
    } catch (err) {
      console.error('Failed to remove membership:', err);
      setMemberships(prev => ({ ...prev, [person.id]: prevList }));
      showToast('Error removing from team.');
    }
  }

  // Possible duplicates. Same full NAME always flags. A shared EMAIL alone
  // does NOT (spouses share inboxes) — it only flags when the names are also
  // similar: identical, or same last name with a nickname-style first name
  // (Kat ↔ Katheryn). Precomputed once per people-list change (n² inside tiny
  // email/name groups only).
  // Staff rows often have the email typed INTO the name cell with a blank
  // email column ("Amber Essman amber.essman@..."), so: names ignore any
  // @-token, and an @-token in the name stands in for a missing email.
  const normName = (n) => (n || '').split(/\s+/).filter(t => !t.includes('@')).join(' ').trim().toLowerCase();
  const effEmail = (p) => {
    const e = (p.email || '').trim().toLowerCase();
    if (e) return e;
    const tok = (p.name || '').split(/\s+/).find(t => t.includes('@'));
    return (tok || '').trim().toLowerCase();
  };
  // One typo in a last name ("Munthanli" vs "Munthali") must not defeat the
  // shared-email dupe check — last names match within edit distance 1.
  // Spouses stay safe: their first names still have to be similar too.
  const lastNear = (a, b) => {
    if (a === b) return true;
    if (Math.abs(a.length - b.length) > 1) return false;
    if (a.length === b.length) {
      let diff = 0;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i] && ++diff > 1) return false;
      return diff === 1;
    }
    const [s, l] = a.length < b.length ? [a, b] : [b, a];
    let i = 0, j = 0, skipped = false;
    while (i < s.length && j < l.length) {
      if (s[i] === l[j]) { i++; j++; }
      else if (skipped) return false;
      else { skipped = true; j++; }
    }
    return true;
  };
  const namesSimilar = (a, b) => {
    const na = normName(a), nb = normName(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    const pa = na.split(' '), pb = nb.split(' ');
    const lastA = pa.slice(1).join(' '), lastB = pb.slice(1).join(' ');
    if (!lastA || !lastB || !lastNear(lastA, lastB)) return false;
    const fa = pa[0], fb = pb[0];
    const shorter = Math.min(fa.length, fb.length);
    return shorter >= 3 && (fa.startsWith(fb) || fb.startsWith(fa));
  };
  const dupeIds = useMemo(() => {
    const ids = new Set();
    const byEmail = {};
    const byName = {};
    people.forEach(p => {
      const e = effEmail(p);
      if (e) (byEmail[e] = byEmail[e] || []).push(p);
      const n = normName(p.name);
      if (n) (byName[n] = byName[n] || []).push(p);
    });
    Object.values(byName).forEach(group => {
      if (group.length > 1) group.forEach(p => ids.add(p.id)); // identical names
    });
    Object.values(byEmail).forEach(group => {
      if (group.length < 2) return;
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          if (namesSimilar(group[i].name, group[j].name)) {
            ids.add(group[i].id);
            ids.add(group[j].id);
          }
        }
      }
    });
    return ids;
  }, [people]);
  const isDupe = (p) => dupeIds.has(p.id);
  const dupeCount = dupeIds.size;

  // Real registration = a numeric Brushfire attendee number. Placeholder text
  // like "staff" in that column does NOT count — everyone registers, staff
  // included. Numeric fallback covers pre-redeploy responses.
  const isReg = (p) => (p.registered !== undefined ? !!p.registered : /^\d+$/.test(String(p.brushfireId || '').trim()));

  const filtered = people.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q);
    const isRegistered = isReg(p);
    // Honor an optimistic just-assigned value so a person drops out of the
    // "No Pass Level" filter the moment you assign them one.
    const effectivePass = passLevelOverrides[p.id] ?? (p.passLevel || '');
    const matchFilter =
      filter === 'all' ? true :
      filter === 'unregistered' ? !isRegistered :
      filter === 'no-pass' ? !effectivePass :
      filter === 'no-bg' ? (isRegistered && !/clear/i.test(p.bgCheck || '')) :
      filter === 'checked-in' ? !!checkIns[p.id] :
      filter === 'not-checked-in' ? (isRegistered && !checkIns[p.id]) :
      filter === 'dupes' ? isDupe(p) : true;
    const matchTeam = ciTeamFilter.length === 0 ||
      (memberships[p.id] || []).some(m => ciTeamFilter.includes(m.teamId));
    return matchSearch && matchFilter && matchTeam;
  });
  if (filter === 'dupes') {
    // Group lookalikes next to each other so pairs are easy to compare.
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }
  if (nameSort) {
    filtered.sort((a, b) => {
      const cmp = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      return nameSort === 'asc' ? cmp : -cmp;
    });
  }

  // Dropdown options must cover every pass a team can confer (e.g. "Security",
  // "Leadership Co"), not just the configured defaults — otherwise a synced
  // value has no matching <option> and the select renders blank.
  const passLevelOptions = Array.from(new Set([
    ...passLevels,
    ...teams.map(t => t.passLevel).filter(Boolean),
  ]));

  const noPassCount = people.filter(
    p => !(passLevelOverrides[p.id] ?? (p.passLevel || ''))
  ).length;

  // Registered people whose background check isn't cleared — the ones Kat needs to chase.
  const noBgCount = people.filter(p => isReg(p) && !/clear/i.test(p.bgCheck || '')).length;

  return (
    <>
      <div style={{ padding: '20px 40px 0', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 16 }}>
          <div>
            <div className="page-title">People</div>
            <div className="page-sub">{people.length} people loaded · 2026</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn"
              onClick={handleSyncBrushfire}
              disabled={syncing}
              title="Pull recently-changed servant leaders from Brushfire right now"
            >
              {syncing ? 'Syncing…' : '↻ Sync Brushfire'}
            </button>
            <button
              className="btn"
              onClick={() => setBgImportOpen(true)}
              title="Upload the background-check CSV to update everyone's status"
            >
              ⇪ Import BG Checks
            </button>
            <button
              className="btn"
              onClick={handleDownloadCheckInCsv}
              disabled={csvBusy}
              title="Printable backup roster: names, teams, badges, radio, meals — with a blank Checked In column"
            >
              {csvBusy ? 'Preparing…' : '⬇ Check-In CSV'}
            </button>
            <button
              className="btn"
              onClick={handleDownloadLcSecurityCsv}
              disabled={lcCsvBusy}
              title="Security list: everyone serving Leadership Co Wed/Thu with their badge access"
            >
              {lcCsvBusy ? 'Preparing…' : '⬇ L-Co Security CSV'}
            </button>
            <button className="btn btn-dark" onClick={() => setAddOpen(true)}>+ Add Person</button>
          </div>
        </div>
        <div style={{ maxWidth: 400, marginBottom: 16 }}>
          <input
            className="search-bar"
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, paddingBottom: 16, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All' },
            { key: 'unregistered', label: 'Not Registered' },
            { key: 'no-pass', label: `No Pass Level${noPassCount ? ` (${noPassCount})` : ''}` },
            { key: 'no-bg', label: `No BG Check${noBgCount ? ` (${noBgCount})` : ''}` },
            { key: 'checked-in', label: `Checked In (${Object.keys(checkIns).filter(id => people.some(p => p.id === id)).length})` },
            { key: 'not-checked-in', label: `Not Checked In (${people.filter(p => isReg(p) && !checkIns[p.id]).length})` },
            { key: 'dupes', label: `Possible Duplicates${dupeCount ? ` (${dupeCount})` : ''}` },
          ].map(f => (
            <button
              key={f.key}
              className={`filter-btn${filter === f.key ? ' active' : ''}`}
              onClick={() => {
                setFilter(f.key);
                if (f.key !== 'checked-in' && f.key !== 'not-checked-in') setCiTeamFilter([]);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {(filter === 'checked-in' || filter === 'not-checked-in') && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 16 }}>
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)' }}>Team</span>
            <TeamFilterDropdown teams={teams} selected={ciTeamFilter} onChange={setCiTeamFilter} />
          </div>
        )}
      </div>

      <div className="page-content" style={{ paddingTop: 24 }}>
        <div className="panel">
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>Loading people…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
              {people.length === 0
                ? 'No people in the database yet. Click + Add Person to add someone.'
                : 'No results match your search.'}
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table compact">
              <colgroup>
                <col style={{ width: '19%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    onClick={() => setNameSort(s => s === 'asc' ? 'desc' : s === 'desc' ? '' : 'asc')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Sort by name (A–Z / Z–A / default order)"
                  >
                    Name{nameSort === 'asc' ? ' ▲' : nameSort === 'desc' ? ' ▼' : ''}
                  </th>
                  <th>Pass Level</th>
                  <th>Teams</th>
                  <th>Registration</th>
                  <th>Checked In</th>
                  <th>BG Check</th>
                  <th>Schedule</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(person => {
                  const passLevel = passLevelOverrides[person.id] ?? (person.passLevel || '');
                  const isRegistered = isReg(person);
                  const personTeams = memberships[person.id] || [];
                  // A schedule link is only meaningful once someone is placed on
                  // a team (they have a roster to view). Gate on that, not on the
                  // legacy person.status field which is never set to "rostered".
                  const isPlaced = personTeams.length > 0;

                  return (
                    <tr key={person.id} onClick={() => openPersonPanel(person)}>
                      <td>
                        <span className="td-name">{person.name}</span>
                        {isDupe(person) && (
                          <>
                            <span className="pill" style={{ fontSize: 9, padding: '2px 7px', marginLeft: 6, background: '#fee', color: '#b00000', border: '1px solid #f5c2c2', verticalAlign: 2 }}>
                              ⚠ Duplicate?
                            </span>
                            <button
                              className="btn btn-xs"
                              style={{ marginLeft: 6, verticalAlign: 2 }}
                              onClick={(e) => { e.stopPropagation(); openMerge(person); }}
                              title="Merge this duplicate into one person — schedules and rosters are combined, nothing is lost"
                            >
                              Merge…
                            </button>
                          </>
                        )}
                        <br />
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{person.email}</span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <PassLevelMultiSelect
                          value={passLevel}
                          options={passLevelOptions}
                          onChange={v => handlePassLevelChange(person, v)}
                        />
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          {personTeams.length === 0 ? (
                            <span style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>No teams</span>
                          ) : personTeams.map(tm => (
                            <span
                              key={tm.teamId}
                              className="pill pill-gray"
                              style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                            >
                              {tm.teamName}
                              <button
                                title={`Remove ${person.name} from ${tm.teamName}`}
                                onClick={() => handleMembershipRemoved(person, tm)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'var(--muted)', fontSize: 11, fontWeight: 700 }}
                                onMouseEnter={e => { e.currentTarget.style.color = '#c00'; }}
                                onMouseLeave={e => { e.currentTarget.style.color = 'var(--muted)'; }}
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                          <AddToTeamDropdown
                            personId={person.id}
                            teams={teams}
                            currentTeamIds={personTeams.map(tm => tm.teamId)}
                            onAdd={(team) => handleMembershipAdded(person.id, team)}
                            showToast={showToast}
                          />
                        </div>
                      </td>
                      <td>
                        {isRegistered
                          ? <span className="pill pill-green">Registered</span>
                          : <span className="pill pill-amber">Not Registered</span>
                        }
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        {checkIns[person.id] ? (
                          <>
                            <span className="pill pill-green" title={`Checked in ${checkIns[person.id].date}${checkIns[person.id].time ? ` at ${checkIns[person.id].time}` : ''}`} style={{ fontSize: 9, padding: '3px 9px' }}>
                              ✓ Yes
                            </span>
                            <button
                              className="btn-remove"
                              title={`Remove today's check-in for ${person.name}`}
                              disabled={ciBusy[person.id]}
                              onClick={() => handleManualUncheck(person)}
                              style={{ marginLeft: 2, fontSize: 11 }}
                            >
                              {ciBusy[person.id] ? '…' : '✕'}
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-xs"
                            disabled={ciBusy[person.id]}
                            onClick={() => handleManualCheckIn(person)}
                            title={`Manually check ${person.name} in for today`}
                          >
                            {ciBusy[person.id] ? 'Saving…' : 'Check In'}
                          </button>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/clear/i.test(person.bgCheck || '') ? (
                          <span className="pill pill-green" style={{ fontSize: 9, padding: '3px 9px', whiteSpace: 'nowrap' }}>✓ Clear</span>
                        ) : person.bgCheck ? (
                          <span className="pill pill-amber" title={person.bgCheck} style={{ fontSize: 9, padding: '3px 9px', whiteSpace: 'nowrap' }}>{person.bgCheck}</span>
                        ) : isRegistered ? (
                          <span className="pill" style={{ background: '#fee', color: '#b00000', border: '1px solid #f5c2c2', fontSize: 9, padding: '3px 9px', whiteSpace: 'nowrap' }}>No BG Check</span>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--subtle)' }}>—</span>
                        )}
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        {isPlaced ? (
                          <>
                            <button
                              className="copy-link-btn"
                              onClick={() => handleCopyLink(person)}
                            >
                              {COPY_ICON} Copy Link
                            </button>
                            <button
                              className={`send-link-btn${!canSend ? ' btn-disabled' : ''}`}
                              onClick={() => handleSendLink(person)}
                              title={canSend ? `Send link to ${person.name}` : 'Only @thebelonging.co staff can send'}
                              style={!canSend ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                            >
                              {SEND_ICON}
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--subtle)' }}>
                            Not on a team yet
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <AddPersonModal open={addOpen} onClose={() => setAddOpen(false)} onSave={handleAddPerson} showToast={showToast} />
      <ImportBgChecksModal
        open={bgImportOpen}
        onClose={() => setBgImportOpen(false)}
        onImported={loadAll}
        showToast={showToast}
      />
      <MergePeopleModal
        open={!!mergeGroup}
        onClose={() => setMergeGroup(null)}
        group={mergeGroup || []}
        memberships={memberships}
        onMerged={loadAll}
        showToast={showToast}
      />
    </>
  );
}
